import React, { useEffect, useMemo, useState } from "react";
import { Move, X, Cog } from "lucide-react";
import { useGame } from "../game/em2c-store";
import { EM2C_BUILDINGS } from "../game/data/em2c-buildings";
import { MOVE_BUILDING_COST, canAffordMoveResources } from "../game/systems/MoveBuildingIntegration";

export default function BuildingContextMenu() {
  const [menu, setMenu] = useState(null);
  const buildings = useGame((s) => s.buildings);
  const resources = useGame((s) => s.resources);
  const startMoveBuilding = useGame((s) => s.startMoveBuilding);

  useEffect(() => {
    const open = (event) => {
      const detail = event.detail || {};
      if (!detail.buildingId) return;
      const width = 250;
      const height = 132;
      const x = Math.max(8, Math.min(window.innerWidth - width - 8, Number(detail.clientX || 0)));
      const y = Math.max(8, Math.min(window.innerHeight - height - 8, Number(detail.clientY || 0)));
      setMenu({ buildingId: detail.buildingId, x, y });
    };
    const close = () => setMenu(null);
    const onKey = (event) => { if (event.key === "Escape") close(); };
    window.addEventListener("mars:building-context", open);
    window.addEventListener("mars:world-click", close);
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("mars:building-context", open);
      window.removeEventListener("mars:world-click", close);
      window.removeEventListener("keydown", onKey);
    };
  }, []);

  const building = useMemo(() => buildings.find((item) => item.id === menu?.buildingId) || null, [buildings, menu]);
  if (!menu || !building) return null;

  const def = EM2C_BUILDINGS[building.type];
  const canAfford = canAffordMoveResources(resources);
  const cost = MOVE_BUILDING_COST.machine_parts;

  return (
    <div
      className="fixed z-[140] w-[250px] border border-cyan-400/35 bg-[#090C10]/98 backdrop-blur-xl shadow-[0_12px_35px_rgba(0,0,0,.65)] pointer-events-auto"
      style={{ left: menu.x, top: menu.y, fontFamily: "Rajdhani, sans-serif" }}
      data-testid="building-context-menu"
      onContextMenu={(e) => e.preventDefault()}
    >
      <div className="flex items-center gap-2 px-3 py-2 border-b border-white/10">
        <div className="w-1.5 h-6 bg-cyan-400 shadow-[0_0_10px_#22d3ee]" />
        <div className="min-w-0 flex-1">
          <div className="text-[9px] font-mono uppercase tracking-widest text-slate-500">Building Action</div>
          <div className="text-xs font-semibold uppercase tracking-wide text-white truncate">{def?.name || building.type}</div>
        </div>
        <button onClick={() => setMenu(null)} className="text-slate-500 hover:text-white"><X size={13} /></button>
      </div>

      <button
        data-testid="context-move-building"
        disabled={!canAfford}
        onClick={() => {
          const started = startMoveBuilding?.(building.id);
          if (started) setMenu(null);
        }}
        className={`w-full px-3 py-3 text-left flex items-center gap-3 border-l-2 border-b ${
          canAfford
            ? "border-l-cyan-400 border-b-cyan-400/20 bg-cyan-500/10 hover:bg-cyan-500/18 text-white"
            : "border-l-slate-600 border-b-white/10 bg-white/[0.02] opacity-50 cursor-not-allowed text-slate-500"
        }`}
      >
        <div className="w-8 h-8 shrink-0 flex items-center justify-center border border-cyan-400/40 bg-cyan-400/10">
          <Move size={18} className="text-cyan-300" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-[12px] font-semibold uppercase tracking-wide text-cyan-200">Move Building</div>
          <div className="text-[9px] font-mono text-slate-400 mt-0.5">Immediate · keeps building state</div>
        </div>
        <div className="flex items-center gap-1 px-2 py-1 border border-[#e8a37e]/35 bg-[#e8a37e]/10 text-[10px] font-mono text-[#e8a37e] shrink-0">
          <Cog size={11} />{cost}
        </div>
      </button>

      {!canAfford && <div className="px-3 py-2 text-[9px] font-mono text-[#ff8c00]">Need {cost} Machine Parts</div>}
    </div>
  );
}
